import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  AvatarGroup,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import InsightsIcon from '@mui/icons-material/Insights';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import CollectionsIcon from '@mui/icons-material/Collections';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import HistoryIcon from '@mui/icons-material/History';
import AddCommentIcon from '@mui/icons-material/AddComment';
import CloseIcon from '@mui/icons-material/Close';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { resources } from '../../core/services/resources';
import { extractJobErrorMessage } from '../../core/services/api';
import { updateViewContext } from '../../core/services/viewContext';
import { EditDocumentDialog } from './EditDocumentDialog';
import { usePermissions } from '../../shared/hooks/usePermissions';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { ConfirmDialog } from '../../shared/components/Dialogs/ConfirmDialog';
import { CardGrid, DashboardCardTile, DASHBOARD_CARD_HEIGHT, dashboardCardColumnSpan } from '../../shared/components/CardGrid';
import { SectionVisualizationsDialog, useVizImage } from './SectionVisualizationsDialog';
import { DocumentEntitiesPanel } from './DocumentEntitiesPanel';
import { AiAssistSectionDialog, type SectionAssistImpactLevel } from './AiAssistSectionDialog';
import { DynamicMuiIcon } from '../../shared/components/DynamicMuiIcon';
import {
  DocumentSectionList,
  DocumentSectionRow,
  ExpandAllSectionsButton,
  buildDocumentMarkdown,
  useCopyMarkdown,
  useExpandAll,
  useSectionOpen,
  type ExpandSignal,
} from '../../shared/components/DocumentSections';
import type {
  AiAssistantRole,
  DocumentAccess,
  DocumentAccessType,
  DocumentCitation,
  DocumentComment,
  DocumentDashboardCard,
  DocumentSection,
  DocumentSectionStatus,
  DocumentVisualization,
  User,
} from '../../core/types/data-models';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatAge(dateStr: string): string {
  const ms = Date.now() - new Date(dateStr).getTime();
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function userInitial(u: User): string {
  return (u.name?.[0] ?? u.email?.[0] ?? '?').toUpperCase();
}

function sectionStatusColor(status: DocumentSectionStatus): 'default' | 'info' | 'warning' | 'success' | 'error' {
  if (status === 'IN_PROGRESS') return 'info';
  if (status === 'IN_REVIEW') return 'warning';
  if (status === 'APPROVED') return 'success';
  if (status === 'ARCHIVED') return 'error';
  return 'default';
}

function sectionStatusLabel(status: DocumentSectionStatus): string {
  if (status === 'IN_PROGRESS') return 'In Progress';
  if (status === 'IN_REVIEW') return 'In Review';
  if (status === 'APPROVED') return 'Approved';
  if (status === 'ARCHIVED') return 'Archived';
  return 'Backlog';
}

// Least-to-most-complete ordering used to roll a subtree's status up to its
// parents — a section is only as "done" as its least-done descendant.
const SECTION_STATUS_ORDER: DocumentSectionStatus[] = [
  'BACKLOG', 'IN_PROGRESS', 'IN_REVIEW', 'APPROVED', 'ARCHIVED',
];

function sectionStatusRank(status: DocumentSectionStatus): number {
  const rank = SECTION_STATUS_ORDER.indexOf(status);
  return rank === -1 ? 0 : rank;
}

/** Maps each section id to the lowest-ranked status found in itself + all of its
 *  descendants, so a parent section visibly reflects the least-complete sub-section
 *  (e.g. a child stuck in IN_REVIEW keeps every ancestor showing IN_REVIEW too). */
function computeEffectiveSectionStatuses(
  sections: DocumentSection[],
  childrenByParent: Map<number, DocumentSection[]>,
): Map<number, DocumentSectionStatus> {
  const effective = new Map<number, DocumentSectionStatus>();
  // Guards against a corrupted parent chain (e.g. two sections pointing at
  // each other) turning this into infinite recursion — a section revisited
  // while its own resolution is still in flight just falls back to its own
  // status instead of recursing again.
  const visiting = new Set<number>();

  const resolve = (section: DocumentSection): DocumentSectionStatus => {
    const cached = effective.get(section.id);
    if (cached) return cached;
    if (visiting.has(section.id)) return section.status ?? 'BACKLOG';
    visiting.add(section.id);

    let lowest = section.status ?? 'BACKLOG';
    for (const child of childrenByParent.get(section.id) ?? []) {
      const childStatus = resolve(child);
      if (sectionStatusRank(childStatus) < sectionStatusRank(lowest)) {
        lowest = childStatus;
      }
    }
    visiting.delete(section.id);
    effective.set(section.id, lowest);
    return lowest;
  };

  sections.forEach(resolve);
  return effective;
}

/** Counts every descendant (children, grandchildren, ...) of `sectionId`, so the
 *  delete confirmation can warn how many sub-sections will be removed with it. */
function countDescendants(sectionId: number, childrenByParent: Map<number, DocumentSection[]>): number {
  const children = childrenByParent.get(sectionId) ?? [];
  return children.reduce((sum, child) => sum + 1 + countDescendants(child.id, childrenByParent), 0);
}

/** Walks up from `sectionId` via parentDocumentSectionId, returning each ancestor
 *  ordered from the immediate parent up to the root. */
function getAncestorChain(sections: DocumentSection[], sectionId: number): DocumentSection[] {
  const byId = new Map(sections.map((s) => [s.id, s]));
  const ancestors: DocumentSection[] = [];
  // visited guards against a corrupted parent chain looping forever.
  const visited = new Set<number>([sectionId]);
  let parentId = byId.get(sectionId)?.parentDocumentSectionId ?? null;
  while (parentId != null && !visited.has(parentId)) {
    const parent = byId.get(parentId);
    if (!parent) break;
    visited.add(parentId);
    ancestors.push(parent);
    parentId = parent.parentDocumentSectionId ?? null;
  }
  return ancestors;
}

/** After `changedSectionId` is saved with `newStatus`, re-derives every ancestor's status
 *  with the same least-complete-wins rule used for the status chip
 *  (computeEffectiveSectionStatuses) and persists any ancestor whose derived status no
 *  longer matches what's stored, so a section approval can promote/demote parent statuses
 *  up the tree without a backend rollup. */
async function syncAncestorStatuses(
  sections: DocumentSection[],
  childrenByParent: Map<number, DocumentSection[]>,
  changedSectionId: number,
  newStatus: DocumentSectionStatus,
): Promise<void> {
  const ancestors = getAncestorChain(sections, changedSectionId);
  if (ancestors.length === 0) return;

  const patchedSections = sections.map((s) =>
    s.id === changedSectionId ? { ...s, status: newStatus } : s,
  );
  const effective = computeEffectiveSectionStatuses(patchedSections, childrenByParent);

  await Promise.all(
    ancestors
      .filter((ancestor) => effective.get(ancestor.id) !== (ancestor.status ?? 'BACKLOG'))
      .map((ancestor) =>
        resources.documentSections.update(ancestor.id, { status: effective.get(ancestor.id) }),
      ),
  );
}

// ── Visualization options ────────────────────────────────────────────────────

const VIZ_COLOR_STYLES = [
  { value: 'blue',       label: 'Professional Blue', instruction: 'Use a professional blue color palette with navy, steel blue, and white tones.' },
  { value: 'vibrant',   label: 'Vibrant',            instruction: 'Use vibrant, high-contrast colors with bold saturated hues.' },
  { value: 'earthy',    label: 'Earthy / Warm',      instruction: 'Use earthy warm tones: oranges, browns, tans, and cream.' },
  { value: 'monochrome',label: 'Monochrome',          instruction: 'Use a monochrome palette of black, white, and shades of gray only.' },
  { value: 'dark',      label: 'Dark / Night',       instruction: 'Use a dark theme with deep backgrounds and bright, vivid accent colors.' },
  { value: 'pastel',    label: 'Pastel / Soft',      instruction: 'Use soft pastel colors: light pinks, blues, yellows, and mints.' },
  { value: 'green',     label: 'Green / Nature',     instruction: 'Use green and teal tones evoking nature, growth, and sustainability.' },
  { value: 'purple',    label: 'Purple / Elegant',   instruction: 'Use an elegant palette of purples, violets, and lavender.' },
  { value: 'red',       label: 'Red / Energy',       instruction: 'Use energetic red and orange tones to convey urgency and action.' },
] as const;

const VIZ_LAYOUTS = [
  { value: 'single-column', label: 'Single Column',        instruction: 'Use a single-column vertical layout with sections stacked top to bottom.' },
  { value: 'two-columns',   label: 'Two Columns',          instruction: 'Use a two-column side-by-side layout for comparison or parallel content.' },
  { value: 'three-columns', label: 'Multiple Columns',     instruction: 'Use a multiple-column grid layout to present multiple items concisely.' },
  { value: 'timeline',      label: 'Timeline / Process',   instruction: 'Use a horizontal timeline or left-to-right process-flow layout with numbered steps and connectors.' },
  { value: 'pyramid',       label: 'Pyramid / Funnel',     instruction: 'Use a pyramid or inverted-funnel shape to represent hierarchy, priority, or narrowing stages.' },
  { value: 'circular',      label: 'Circular / Hub & Spoke', instruction: 'Use a circular hub-and-spoke layout with a central concept and ideas radiating outward.' },
  { value: 'grid',          label: 'Grid / Matrix',        instruction: 'Use a grid or matrix layout with clearly labeled rows and columns.' },
  { value: 'comparison',    label: 'Comparison Table',     instruction: 'Use a structured side-by-side comparison table layout.' },
  { value: 'steps',         label: 'Steps / Numbered',     instruction: 'Use a numbered-steps layout to show a clear sequential process or checklist.' },
  { value: 'mindmap',       label: 'Mind Map / Tree',      instruction: 'Use a tree or mind-map layout with a root node and branching hierarchy.' },
  { value: 'infographic',   label: 'Infographic',          instruction: 'Use a rich mixed infographic layout combining icons, stats, text blocks, and visual elements.' },
] as const;

type VizColorValue = (typeof VIZ_COLOR_STYLES)[number]['value'] | '';
type VizLayoutValue = (typeof VIZ_LAYOUTS)[number]['value'] | '';

// ── Section row (recursive for sub-sections) ─────────────────────────────────

function SectionRow({
  section,
  sections,
  childrenByParent,
  effectiveStatusById,
  depth = 0,
  expandSignal,
  usersMap,
  visualizationsBySection,
  allComments,
  onDelete,
  assistantRole,
}: {
  section: DocumentSection;
  /** Flat list of every section in the document — used to walk up to ancestors when a status change needs to roll up. */
  sections: DocumentSection[];
  childrenByParent: Map<number, DocumentSection[]>;
  /** Section id → lowest-ranked status across itself and all descendants; see computeEffectiveSectionStatuses. */
  effectiveStatusById: Map<number, DocumentSectionStatus>;
  depth?: number;
  expandSignal?: ExpandSignal | null;
  usersMap: Map<number, User>;
  visualizationsBySection: Map<number, DocumentVisualization[]>;
  allComments: DocumentComment[];
  onDelete: (id: number) => void;
  /** The document's category-matched AI Assistant Role — null if none is configured. */
  assistantRole: AiAssistantRole | null;
}) {
  const queryClient = useQueryClient();
  const children = (childrenByParent.get(section.id) ?? []).sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );
  const hasContent = !!section.content?.trim();
  const [open, setOpen] = useSectionOpen(false, expandSignal);
  const { canEdit } = usePermissions();
  const [editing, setEditing] = useState(false);
  const [draftContent, setDraftContent] = useState(section.content ?? '');
  const [draftTitle, setDraftTitle] = useState(section.title ?? '');
  const [saving, setSaving] = useState(false);
  const [autoApprove, setAutoApprove] = useState(false);
  const [aiAssistOpen, setAiAssistOpen] = useState(false);
  const [lastImpactLevel, setLastImpactLevel] = useState<SectionAssistImpactLevel | null>(null);
  const [lastCitations, setLastCitations] = useState<DocumentCitation[] | null>(null);
  const [impactNoticeOpen, setImpactNoticeOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [restructuring, setRestructuring] = useState(false);
  const [mergeConfirmOpen, setMergeConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [vizDialogOpen, setVizDialogOpen] = useState(false);
  const [vizInstructions, setVizInstructions] = useState('');
  const [vizSubmitting, setVizSubmitting] = useState(false);
  const [vizError, setVizError] = useState<string | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [vizColorStyle, setVizColorStyle] = useState<VizColorValue>('');
  const [vizLayout, setVizLayout] = useState<VizLayoutValue>('');
  const [vizSelectedSubSections, setVizSelectedSubSections] = useState<Set<number>>(new Set());
  const [addCommentOpen, setAddCommentOpen] = useState(false);
  const [addCommentBody, setAddCommentBody] = useState('');
  const [addCommentSubmitting, setAddCommentSubmitting] = useState(false);
  const [commentsPanelOpen, setCommentsPanelOpen] = useState(false);

  const isDirty = draftContent !== (section.content ?? '') || draftTitle.trim() !== (section.title ?? '');

  const hasChildren = children.length > 0;
  const isExpandable = editing || hasContent || children.length > 0;
  const descendantCount = countDescendants(section.id, childrenByParent);

  const visualizations = visualizationsBySection.get(section.id) ?? [];

  // Poll the last visualization-generation job so a running generation shows a
  // spinner (and blocks re-runs) even after navigating back to the document.
  const vizJobQuery = useQuery({
    queryKey: ['job', section.visualizationJobId],
    queryFn: () => resources.getJob(section.visualizationJobId!),
    enabled: section.visualizationJobId != null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'PENDING' || status === 'PROCESSING' ? 3000 : false;
    },
  });
  const vizJobStatus = vizJobQuery.data?.status;
  const vizJobActive = vizJobStatus === 'PENDING' || vizJobStatus === 'PROCESSING';

  // When the job finishes, pull in the freshly created visualization.
  useEffect(() => {
    if (vizJobStatus === 'COMPLETED') {
      queryClient.invalidateQueries({ queryKey: ['document-visualizations', section.documentId] });
    }
  }, [vizJobStatus, section.documentId, queryClient]);
  const isApproved = section.status === 'APPROVED';
  const lastEditor = section.lastUpdatedByUserId
    ? usersMap.get(section.lastUpdatedByUserId)
    : null;
  const assignedUser = section.assignUserId != null
    ? usersMap.get(section.assignUserId)
    : null;

  useEffect(() => {
    setDraftContent(section.content ?? '');
  }, [section.content]);

  useEffect(() => {
    setDraftTitle(section.title ?? '');
  }, [section.title]);

  const saveContent = async (status: 'APPROVED' | 'IN_REVIEW') => {
    setSaving(true);
    try {
      await resources.documentSections.update(section.id, {
        content: draftContent,
        title: draftTitle.trim(),
        status,
        ...(lastCitations ? { citations: lastCitations } : {}),
      });
      await syncAncestorStatuses(sections, childrenByParent, section.id, status);
      queryClient.invalidateQueries({ queryKey: ['document-sections', section.documentId] });
    } finally {
      setSaving(false);
    }
  };

  const handleDone = async () => {
    if (isDirty) {
      // Skip this notice when "Approve and run impact analysis" is checked —
      // that path already runs the real analysis server-side, so there's
      // nothing to warn about.
      if (!autoApprove && (lastImpactLevel === 'MEDIUM' || lastImpactLevel === 'HIGH')) {
        setImpactNoticeOpen(true);
        return;
      }
      await saveContent(autoApprove ? 'APPROVED' : 'IN_REVIEW');
    }
    setEditing(false);
    setLastImpactLevel(null);
    setLastCitations(null);
  };

  const handleImpactNoticeProceed = async () => {
    setImpactNoticeOpen(false);
    await saveContent(autoApprove ? 'APPROVED' : 'IN_REVIEW');
    setEditing(false);
    setLastImpactLevel(null);
    setLastCitations(null);
  };

  const handleImpactNoticeCancel = () => setImpactNoticeOpen(false);

  const handleAiAssistApply = (
    content: string,
    impactLevel: SectionAssistImpactLevel,
    citations: DocumentCitation[] | null,
  ) => {
    setDraftContent(content);
    setLastImpactLevel(impactLevel);
    setLastCitations(citations);
    setAiAssistOpen(false);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditing(true);
    setOpen(true);
    setAutoApprove(false);
    setLastImpactLevel(null);
    setLastCitations(null);
  };

  const handleClose = () => {
    if (isDirty) {
      setCloseConfirmOpen(true);
    } else {
      setEditing(false);
    }
  };

  const handleCloseConfirm = () => {
    setCloseConfirmOpen(false);
    setDraftContent(section.content ?? '');
    setDraftTitle(section.title ?? '');
    setEditing(false);
    setLastImpactLevel(null);
  };

  const handleCloseCancel = () => {
    setCloseConfirmOpen(false);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    onDelete(section.id);
  };

  const handleApprove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setSaving(true);
    try {
      await resources.documentSections.update(section.id, { status: 'APPROVED' });
      await syncAncestorStatuses(sections, childrenByParent, section.id, 'APPROVED');
      await queryClient.invalidateQueries({ queryKey: ['document-sections', section.documentId] });
    } finally {
      setSaving(false);
    }
  };

  const handleSplit = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setRestructuring(true);
    try {
      await resources.splitDocumentSection(section.id);
      await queryClient.invalidateQueries({ queryKey: ['document-sections', section.documentId] });
      setOpen(true);
    } finally {
      setRestructuring(false);
    }
  };

  const handleMergeConfirm = async () => {
    setMergeConfirmOpen(false);
    setRestructuring(true);
    try {
      await resources.mergeDocumentSection(section.id);
      await queryClient.invalidateQueries({ queryKey: ['document-sections', section.documentId] });
      setOpen(true);
    } finally {
      setRestructuring(false);
    }
  };

  const handleGenerateVisualization = async (e: React.FormEvent) => {
    e.preventDefault();
    setVizSubmitting(true);
    setVizError(null);
    try {
      // Build the full instruction from style/layout choices + the user's own text.
      // The user never sees the injected style/layout sentences.
      const parts: string[] = [];
      const colorEntry = VIZ_COLOR_STYLES.find((c) => c.value === vizColorStyle);
      const layoutEntry = VIZ_LAYOUTS.find((l) => l.value === vizLayout);
      if (colorEntry) parts.push(colorEntry.instruction);
      if (layoutEntry) parts.push(layoutEntry.instruction);
      if (vizInstructions.trim()) parts.push(vizInstructions.trim());

      // Collect all descendant IDs of the selected direct children so the backend
      // knows exactly which sub-sections to include in the prompt.
      const collectSubtreeIds = (parentId: number): number[] => {
        const ids = [parentId];
        for (const child of (childrenByParent.get(parentId) ?? [])) {
          ids.push(...collectSubtreeIds(child.id));
        }
        return ids;
      };
      const includedSubSectionIds = hasChildren
        ? Array.from(vizSelectedSubSections).flatMap((id) => collectSubtreeIds(id))
        : undefined;

      await resources.generateSectionVisualization(section.id, {
        userInstructions: parts.length > 0 ? parts.join(' ') : undefined,
        includedSubSectionIds,
      });
      // Refetch sections so this row picks up the new visualizationJobId and the
      // running indicator/poll kicks in.
      await queryClient.invalidateQueries({ queryKey: ['document-sections', section.documentId] });
      setVizDialogOpen(false);
      setVizInstructions('');
      setVizColorStyle('');
      setVizLayout('');
      setVizSelectedSubSections(new Set());
    } catch (err) {
      setVizError(extractJobErrorMessage(err));
    } finally {
      setVizSubmitting(false);
    }
  };

  const openVizDialog = (e: React.MouseEvent) => {
    e.stopPropagation();
    setVizError(null);
    setVizColorStyle('');
    setVizLayout('');
    setVizSelectedSubSections(new Set(children.map((c) => c.id)));
    setVizDialogOpen(true);
  };

  const sectionComments = allComments.filter(
    (c) => c.documentSectionId === section.id && c.parentId === null,
  );
  const openCommentsCount = sectionComments.filter((c) => !c.resolvedAt).length;

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addCommentBody.trim()) return;
    setAddCommentSubmitting(true);
    try {
      await resources.documentCommentsFor(section.documentId).create({
        body: addCommentBody.trim(),
        documentSectionId: section.id,
        parentId: null,
      } as Partial<DocumentComment>);
      queryClient.invalidateQueries({ queryKey: ['document-comments', section.documentId] });
      setAddCommentBody('');
      setAddCommentOpen(false);
    } finally {
      setAddCommentSubmitting(false);
    }
  };

  const handleResolveComment = async (id: number) => {
    await resources.resolveDocumentComment(section.documentId, id, true);
    queryClient.invalidateQueries({ queryKey: ['document-comments', section.documentId] });
  };

  return (
    <>
      <DocumentSectionRow
        section={section}
        depth={depth}
        open={open || commentsPanelOpen}
        expandable={isExpandable}
        toggleDisabled={editing}
        onToggle={() => setOpen((o) => !o)}
        onTitleClick={() =>
          updateViewContext({
            documentSectionId: section.id,
            documentSectionTitle: section.title,
            documentSectionIsRoot: section.parentDocumentSectionId == null,
          })
        }
        titleAdornments={
          <>
            {section.aiSummary && (
              <Tooltip
                title={section.aiSummary}
                arrow
                placement="right"
                slotProps={{ tooltip: { sx: { fontSize: 13, lineHeight: 1.5, maxWidth: 360 } } }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help', flexShrink: 0 }} />
              </Tooltip>
            )}

            <Chip
              label={sectionStatusLabel(effectiveStatusById.get(section.id) ?? section.status ?? 'BACKLOG')}
              size="small"
              color={sectionStatusColor(effectiveStatusById.get(section.id) ?? section.status ?? 'BACKLOG')}
              variant="outlined"
              sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
            />

            {assignedUser && (
              <Tooltip title={`Assigned to ${assignedUser.name ?? assignedUser.email}`}>
                <Avatar
                  sx={{
                    width: 20, height: 20, fontSize: 10,
                    bgcolor: 'secondary.main', flexShrink: 0,
                  }}
                >
                  {userInitial(assignedUser)}
                </Avatar>
              </Tooltip>
            )}

            {visualizations.length > 0 && (
              <Tooltip title={`View ${visualizations.length} visualization${visualizations.length > 1 ? 's' : ''}`}>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setGalleryOpen(true); }}
                  sx={{ width: 24, height: 24, flexShrink: 0, color: 'var(--color-muted)' }}
                >
                  <Badge badgeContent={visualizations.length} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: 8, height: 13, minWidth: 13 } }}>
                    <CollectionsIcon sx={{ fontSize: 16 }} />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}

            {vizJobActive && (
              <Tooltip title="Generating visualization…">
                <CircularProgress size={14} sx={{ flexShrink: 0 }} />
              </Tooltip>
            )}

            {openCommentsCount > 0 && (
              <Tooltip title={`${openCommentsCount} open comment${openCommentsCount > 1 ? 's' : ''}`}>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setCommentsPanelOpen(true); setOpen(true); }}
                  sx={{ width: 24, height: 24, flexShrink: 0, color: 'warning.main' }}
                >
                  <Badge badgeContent={openCommentsCount} color="warning" sx={{ '& .MuiBadge-badge': { fontSize: 8, height: 13, minWidth: 13 } }}>
                    <CommentOutlinedIcon sx={{ fontSize: 16 }} />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
          </>
        }
        trailing={
          <>
            {/* last-editor avatar */}
            {lastEditor && (
              <Tooltip title={`Last edited by ${lastEditor.name ?? lastEditor.email}`}>
                <Avatar
                  sx={{
                    width: 20, height: 20, fontSize: 10,
                    bgcolor: 'primary.main', mr: 0.5, flexShrink: 0,
                  }}
                >
                  {userInitial(lastEditor)}
                </Avatar>
              </Tooltip>
            )}

            {saving && (
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, flexShrink: 0 }}>
                Saving…
              </Typography>
            )}

            {restructuring && (
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, flexShrink: 0 }}>
                {hasChildren ? 'Merging…' : 'Splitting…'}
              </Typography>
            )}
          </>
        }
        toolbar={
          <>
            {/* AI / content actions */}
            <Tooltip title={vizJobActive ? 'Generating visualization…' : 'Visualize — generate infographic'}>
              <span>
                <IconButton
                  size="small"
                  sx={{ width: 26, height: 26 }}
                  onClick={openVizDialog}
                  disabled={vizJobActive}
                >
                  <AutoFixHighIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Analyze — compare with document and recommend updates">
              <IconButton size="small" sx={{ width: 26, height: 26 }}>
                <InsightsIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            {hasChildren ? (
              <Tooltip title="Merge — fold sub-sections into this section">
                <span>
                  <IconButton
                    size="small"
                    sx={{ width: 26, height: 26 }}
                    onClick={() => setMergeConfirmOpen(true)}
                    disabled={restructuring}
                  >
                    <CallMergeIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title={hasContent ? 'Split — divide into sub-sections' : 'Add content before splitting'}>
                <span>
                  <IconButton
                    size="small"
                    sx={{ width: 26, height: 26 }}
                    onClick={handleSplit}
                    disabled={!hasContent || restructuring}
                  >
                    <CallSplitIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            <Divider orientation="vertical" flexItem sx={{ my: '3px' }} />

            {/* Workflow actions */}
            <Tooltip title="Assign for review">
              <IconButton size="small" sx={{ width: 26, height: 26 }}>
                <AssignmentIndIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={isApproved ? 'Already approved' : 'Approve changes'}>
              <span>
                <IconButton
                  size="small"
                  sx={{ width: 26, height: 26 }}
                  onClick={handleApprove}
                  disabled={isApproved || saving}
                >
                  <TaskAltIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Version history">
              <IconButton size="small" sx={{ width: 26, height: 26 }}>
                <HistoryIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ my: '3px' }} />

            {/* Edit — hidden for roles without project:edit, so a viewer sees a
                fully rendered section with no affordance that would 403. */}
            {canEdit && (
              <>
                <Tooltip title="Edit content">
                  <IconButton size="small" sx={{ width: 26, height: 26 }} onClick={handleEdit}>
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ my: '3px' }} />
              </>
            )}

            {/* Add comment */}
            <Tooltip title="Add comment">
              <IconButton
                size="small"
                sx={{ width: 26, height: 26 }}
                onClick={(e) => { e.stopPropagation(); setAddCommentOpen(true); }}
              >
                <AddCommentIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ my: '3px' }} />

            {/* Delete */}
            <Tooltip title="Delete section">
              <IconButton
                size="small"
                sx={{ width: 26, height: 26, color: 'error.main' }}
                onClick={handleDelete}
              >
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </>
        }
        body={
          editing ? (
            <Box sx={{ py: 1, pr: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Section title"
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                disabled={saving}
                error={!draftTitle.trim()}
                helperText={!draftTitle.trim() ? 'Title is required' : ' '}
                sx={{ mb: 1 }}
              />
              <TextField
                multiline
                fullWidth
                size="small"
                value={draftContent}
                onChange={(e) => setDraftContent(e.target.value)}
                placeholder="Add content…"
                minRows={3}
                disabled={saving}
                autoFocus
              />
              <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ mt: 0.75 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => { e.stopPropagation(); setAiAssistOpen(true); }}
                  disabled={saving}
                  startIcon={<DynamicMuiIcon iconName={assistantRole?.avatarIcon} sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: 'none',
                    mr: 'auto',
                    borderColor: assistantRole?.color || 'primary.main',
                    color: assistantRole?.color || 'primary.main',
                  }}
                >
                  Ask AI Assistant to help
                </Button>
                {isDirty && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={autoApprove}
                        onChange={(e) => setAutoApprove(e.target.checked)}
                        sx={{ p: '4px' }}
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ userSelect: 'none' }}>
                        Approve and run impact analysis
                      </Typography>
                    }
                    sx={{ mr: 0 }}
                  />
                )}
                <Button size="small" variant="contained" onClick={handleDone} disabled={saving || !draftTitle.trim()}>
                  Done
                </Button>
                <Button size="small" variant="outlined" color="inherit" onClick={handleClose} disabled={saving}>
                  Close
                </Button>
              </Stack>
            </Box>
          ) : undefined
        }
        aside={
          commentsPanelOpen && (
            <Box
              sx={{
                width: 280,
                flexShrink: 0,
                borderLeft: '1px solid',
                borderColor: 'divider',
                pl: 2,
                pr: 1.5,
                pt: 1,
                pb: 2,
                maxHeight: 420,
                overflowY: 'auto',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <CommentOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                  <Typography variant="subtitle2">Comments</Typography>
                  {openCommentsCount > 0 && (
                    <Chip
                      size="small"
                      label={openCommentsCount}
                      color="warning"
                      sx={{ height: 16, fontSize: 10 }}
                    />
                  )}
                </Stack>
                <IconButton size="small" onClick={() => setCommentsPanelOpen(false)}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Stack>

              {sectionComments.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  No comments yet.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {sectionComments.map((comment) => {
                    const author = usersMap.get(comment.authorUserId);
                    return (
                      <Box key={comment.id} sx={{ opacity: comment.resolvedAt ? 0.55 : 1 }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <Avatar
                            sx={{ width: 24, height: 24, fontSize: 10, bgcolor: 'primary.main', flexShrink: 0 }}
                          >
                            {author ? userInitial(author) : 'U'}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                              <Typography variant="caption" fontWeight={600} noWrap>
                                {author?.name ?? author?.email ?? 'User'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                · {formatAge(comment.createdAt)}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{ mt: 0.25, whiteSpace: 'pre-wrap', fontSize: 12, color: 'var(--color-ink-2)' }}
                            >
                              {comment.body}
                            </Typography>
                            {comment.resolvedAt ? (
                              <Chip
                                size="small"
                                color="success"
                                label="Resolved"
                                sx={{ mt: 0.5, height: 16, fontSize: 9 }}
                              />
                            ) : (
                              <Button
                                size="small"
                                variant="text"
                                sx={{ mt: 0.25, p: 0, fontSize: 11, minWidth: 0, height: 22 }}
                                onClick={() => void handleResolveComment(comment.id)}
                              >
                                Resolve
                              </Button>
                            )}
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          )
        }
      >
        {children.map((child) => (
          <SectionRow
            key={child.id}
            section={child}
            sections={sections}
            childrenByParent={childrenByParent}
            effectiveStatusById={effectiveStatusById}
            depth={depth + 1}
            expandSignal={expandSignal}
            usersMap={usersMap}
            visualizationsBySection={visualizationsBySection}
            allComments={allComments}
            onDelete={onDelete}
            assistantRole={assistantRole}
          />
        ))}
      </DocumentSectionRow>

      {/* ── Discard-changes confirmation ── */}
      <Dialog open={closeConfirmOpen} onClose={handleCloseCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Discard changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes in this section. Close without saving?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancel}>Stay</Button>
          <Button onClick={handleCloseConfirm} color="error" variant="contained" size="small">
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Merge confirmation ── */}
      <Dialog open={mergeConfirmOpen} onClose={() => setMergeConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Merge sub-sections?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The {children.length} sub-section{children.length > 1 ? 's' : ''} will be folded into
            this section as bullet points and deleted. This can’t be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleMergeConfirm} color="error" variant="contained" size="small">
            Merge
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete confirmation ── */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete section?"
        message={
          descendantCount > 0
            ? `"${section.title}" and its ${descendantCount} sub-section${descendantCount > 1 ? 's' : ''} will be permanently deleted. This can't be undone.`
            : `"${section.title}" will be permanently deleted. This can't be undone.`
        }
        confirmLabel="Delete"
        destructive
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteConfirmOpen(false)}
      />

      {/* ── Ask AI Assistant to help ── */}
      <AiAssistSectionDialog
        open={aiAssistOpen}
        onClose={() => setAiAssistOpen(false)}
        sectionId={section.id}
        initialContent={draftContent}
        role={assistantRole}
        onApply={handleAiAssistApply}
      />

      {/* ── Possible impact on other sections ── */}
      <Dialog open={impactNoticeOpen} onClose={handleImpactNoticeCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Possible impact on other sections</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The AI assistant flagged this change as {lastImpactLevel === 'HIGH' ? 'high' : 'medium'} impact
            — it could affect other sections related to this one. We recommend running impact analysis
            before finalizing.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImpactNoticeCancel}>Go back</Button>
          <Button onClick={handleImpactNoticeProceed} color="warning" variant="contained" size="small">
            Save anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Generate-visualization instructions ── */}
      <FormDialog
        open={vizDialogOpen}
        title="Generate visualization"
        onClose={() => setVizDialogOpen(false)}
        onSubmit={handleGenerateVisualization}
        submitLabel="Generate"
        loading={vizSubmitting}
      >
        <Typography variant="body2" color="text.secondary">
          Generate an infographic from this section and its sub-sections. Optionally choose
          a color style and layout, then add any extra instructions — or leave everything blank.
        </Typography>

        {/* Color style */}
        <FormControl size="small" fullWidth>
          <InputLabel>Color style (optional)</InputLabel>
          <Select
            label="Color style (optional)"
            value={vizColorStyle}
            onChange={(e) => setVizColorStyle(e.target.value as VizColorValue)}
            disabled={vizSubmitting}
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {VIZ_COLOR_STYLES.map((c) => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Layout */}
        <FormControl size="small" fullWidth>
          <InputLabel>Layout (optional)</InputLabel>
          <Select
            label="Layout (optional)"
            value={vizLayout}
            onChange={(e) => setVizLayout(e.target.value as VizLayoutValue)}
            disabled={vizSubmitting}
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {VIZ_LAYOUTS.map((l) => (
              <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sub-sections to include — only shown when there are direct children */}
        {hasChildren && (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
              Include sub-sections
            </Typography>
            <Stack spacing={0}>
              {children.map((child) => (
                <FormControlLabel
                  key={child.id}
                  disabled={vizSubmitting}
                  control={
                    <Checkbox
                      size="small"
                      checked={vizSelectedSubSections.has(child.id)}
                      onChange={(e) =>
                        setVizSelectedSubSections((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(child.id);
                          else next.delete(child.id);
                          return next;
                        })
                      }
                      sx={{ p: '4px' }}
                    />
                  }
                  label={<Typography variant="body2">{child.title}</Typography>}
                  sx={{ ml: 0.5, mr: 0 }}
                />
              ))}
            </Stack>
          </Box>
        )}

        <TextField
          label="Instructions (optional)"
          value={vizInstructions}
          onChange={(e) => setVizInstructions(e.target.value)}
          placeholder="e.g. Emphasize the risks; keep it concise…"
          multiline
          minRows={3}
          fullWidth
          disabled={vizSubmitting}
        />
        {vizError && (
          <Typography variant="caption" color="error">{vizError}</Typography>
        )}
      </FormDialog>

      {/* ── Add comment dialog ── */}
      <FormDialog
        open={addCommentOpen}
        title="Add comment"
        onClose={() => { setAddCommentOpen(false); setAddCommentBody(''); }}
        onSubmit={handleAddComment}
        submitLabel="Post"
        loading={addCommentSubmitting}
      >
        <TextField
          label="Comment"
          value={addCommentBody}
          onChange={(e) => setAddCommentBody(e.target.value)}
          placeholder="Leave a comment on this section…"
          multiline
          minRows={3}
          fullWidth
          autoFocus
          disabled={addCommentSubmitting}
        />
      </FormDialog>

      {/* ── Visualizations gallery ── */}
      <SectionVisualizationsDialog
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        sectionTitle={section.title}
        visualizations={visualizations}
        docId={section.documentId}
      />
    </>
  );
}

// ── Shared users block ────────────────────────────────────────────────────────

function SharedUsersBlock({ docId }: { docId: number }) {
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [selectedAccessType, setSelectedAccessType] = useState<DocumentAccessType>('VIEW');
  const [adding, setAdding] = useState(false);

  const accessQuery = useQuery({
    queryKey: ['document-access', docId],
    queryFn: () =>
      resources.documentAccess.list({ filter: { documentId: [docId] }, pageSize: 100 }),
  });

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => resources.users.list({ pageSize: 500 }),
  });

  const accessList: DocumentAccess[] = accessQuery.data?.data ?? [];
  const allUsers: User[] = usersQuery.data?.data ?? [];
  const sharedUserIds = new Set(accessList.map((a) => a.userId));
  const sharedUsers = allUsers.filter((u) => sharedUserIds.has(u.id));
  const availableUsers = allUsers.filter((u) => !sharedUserIds.has(u.id));

  const handleAdd = async () => {
    if (!selectedUserId) return;
    setAdding(true);
    try {
      await resources.documentAccess.create({
        documentId: docId,
        userId: selectedUserId as number,
        accessType: selectedAccessType,
      } as Partial<DocumentAccess>);
      await queryClient.invalidateQueries({ queryKey: ['document-access', docId] });
      setSelectedUserId('');
      setAnchorEl(null);
    } finally {
      setAdding(false);
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {sharedUsers.length > 0 && (
        <AvatarGroup
          max={4}
          sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 11 } }}
        >
          {sharedUsers.map((u) => {
            const access = accessList.find((a) => a.userId === u.id);
            return (
              <Tooltip key={u.id} title={`${u.name ?? u.email} · ${access?.accessType ?? ''}`}>
                <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: 11 }}>
                  {userInitial(u)}
                </Avatar>
              </Tooltip>
            );
          })}
        </AvatarGroup>
      )}

      <Tooltip title="Share with user">
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <PersonAddIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 0.5 } }}
      >
        <Box sx={{ p: 2, width: 260 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Share document</Typography>
          <Stack spacing={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel>User</InputLabel>
              <Select
                label="User"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value as number | '')}
              >
                {availableUsers.length === 0 ? (
                  <MenuItem disabled value="">All users already have access</MenuItem>
                ) : (
                  availableUsers.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.name ?? u.email}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Access level</InputLabel>
              <Select
                label="Access level"
                value={selectedAccessType}
                onChange={(e) => setSelectedAccessType(e.target.value as DocumentAccessType)}
              >
                <MenuItem value="VIEW">View</MenuItem>
                <MenuItem value="EDIT">Edit</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              fullWidth
              disabled={!selectedUserId || adding}
              onClick={handleAdd}
            >
              Share
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Stack>
  );
}

// ── Dashboard tab ────────────────────────────────────────────────────────────

function VisualizationThumbCard({
  viz,
  sectionTitle,
  onClick,
}: {
  viz: DocumentVisualization;
  sectionTitle: string;
  onClick: () => void;
}) {
  const { data } = useVizImage(viz.id, true);
  const src = data?.imageData ? `data:${data.mimeType};base64,${data.imageData}` : undefined;

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Box
        sx={{
          aspectRatio: '4 / 3',
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {src
          ? <Box component="img" src={src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <Skeleton variant="rectangular" width="100%" height="100%" />}
      </Box>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          px: 1,
          py: 0.5,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {sectionTitle}
      </Typography>
    </Box>
  );
}

/** Dashboard tab: document-level insight cards generated by the
 * GENERATE_DOCUMENT_METADATA job, followed by a grid of every
 * visualization in the document. */
function DashboardTab({
  docId,
  visualizations,
  sectionTitleById,
  onSelectVisualization,
}: {
  docId: number;
  visualizations: DocumentVisualization[];
  sectionTitleById: Map<number, string>;
  onSelectVisualization: (viz: DocumentVisualization) => void;
}) {
  const queryClient = useQueryClient();
  const { canEdit } = usePermissions();

  const [regenConfirmOpen, setRegenConfirmOpen] = useState(false);
  const [regenJobId, setRegenJobId] = useState<number | null>(null);
  const [regenError, setRegenError] = useState<string | null>(null);

  const cardsQuery = useQuery({
    queryKey: ['document-dashboard-cards', docId],
    queryFn: () =>
      resources.documentDashboardCards.list({
        filter: { documentId: [docId] },
        sort: 'order',
        pageSize: 100,
      }),
    enabled: Number.isFinite(docId),
  });
  const cards: DocumentDashboardCard[] = cardsQuery.data?.data ?? [];

  const invalidateCards = () => queryClient.invalidateQueries({ queryKey: ['document-dashboard-cards', docId] });

  // Reorders cards by swapping `order` with the adjacent card (by current
  // display order) — same swap pattern as ProjectDetail's High Level Overview.
  const moveCard = useMutation({
    mutationFn: async ({ id, direction }: { id: number; direction: 'before' | 'after' }): Promise<void> => {
      const sorted = [...cards].sort((a, b) => a.order - b.order);
      const index = sorted.findIndex((c) => c.id === id);
      const swapIndex = direction === 'before' ? index - 1 : index + 1;
      if (index === -1 || swapIndex < 0 || swapIndex >= sorted.length) return;
      const current = sorted[index];
      const neighbor = sorted[swapIndex];
      await Promise.all([
        resources.documentDashboardCards.update(current.id, { order: neighbor.order }),
        resources.documentDashboardCards.update(neighbor.id, { order: current.order }),
      ]);
    },
    onSuccess: invalidateCards,
  });

  const resizeCard = useMutation({
    mutationFn: ({ id, columnSpan }: { id: number; columnSpan: number }) =>
      resources.documentDashboardCards.update(id, { columnSpan }),
    onSuccess: invalidateCards,
  });

  // Poll the generation job so the button shows progress until it finishes,
  // then refresh the cards below.
  const regenJobQuery = useQuery({
    queryKey: ['job', regenJobId],
    queryFn: () => resources.getJob(regenJobId!),
    enabled: regenJobId != null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'PENDING' || status === 'PROCESSING' ? 3000 : false;
    },
  });
  const regenJobStatus = regenJobQuery.data?.status;
  const regenerating = regenJobId != null && regenJobStatus !== 'COMPLETED' && regenJobStatus !== 'FAILED';

  useEffect(() => {
    if (regenJobStatus === 'COMPLETED') {
      queryClient.invalidateQueries({ queryKey: ['document-dashboard-cards', docId] });
    } else if (regenJobStatus === 'FAILED') {
      setRegenError(regenJobQuery.data?.error ?? 'Failed to generate dashboard cards');
    }
  }, [regenJobStatus, regenJobQuery.data?.error, docId, queryClient]);

  const handleRegenerate = async () => {
    setRegenConfirmOpen(false);
    setRegenError(null);
    try {
      const { jobId } = await resources.generateDocumentMetadata(docId);
      setRegenJobId(jobId);
    } catch (err) {
      setRegenError(extractJobErrorMessage(err));
    }
  };

  // ── Delete cards ──
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteCards = async () => {
    setDeleteConfirmOpen(false);
    setDeleting(true);
    setDeleteError(null);
    try {
      await resources.deleteDocumentDashboardCards(docId);
      await queryClient.invalidateQueries({ queryKey: ['document-dashboard-cards', docId] });
    } catch (err) {
      setDeleteError(extractJobErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* ── Insight cards ── */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              fontFamily: 'var(--font-sans)',
              fontSize: 18,
              fontWeight: 700,
              color: 'var(--color-ink)',
            }}
          >
            Insights
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1}>
            {regenerating && (
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  {regenJobQuery.data?.progressMessage ?? 'Preparing…'}
                </Typography>
              </Stack>
            )}
            {regenError && (
              <Typography variant="caption" color="error">{regenError}</Typography>
            )}
            {deleteError && (
              <Typography variant="caption" color="error">{deleteError}</Typography>
            )}
            {cards.length > 0 && (
              <Tooltip title="Delete all of this document's dashboard cards">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteSweepIcon />}
                    onClick={() => setDeleteConfirmOpen(true)}
                    disabled={deleting || regenerating}
                  >
                    Delete Cards
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Generate dashboard cards from the document's content">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AutorenewIcon />}
                  onClick={() => setRegenConfirmOpen(true)}
                  disabled={regenerating}
                >
                  {cards.length > 0 ? 'Regenerate Cards' : 'Generate Cards'}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {cardsQuery.isLoading ? (
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            {[1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" width={220} height={96} sx={{ borderRadius: 1 }} />)}
          </Stack>
        ) : cards.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            No dashboard cards yet — generate them from the document's content.
          </Typography>
        ) : (
          <CardGrid
            items={cards}
            getId={(card) => card.id}
            getColumnSpan={(card) => card.columnSpan ?? dashboardCardColumnSpan(card)}
            rowHeight={DASHBOARD_CARD_HEIGHT}
            editable={canEdit}
            onMove={canEdit ? (card, direction) => moveCard.mutate({ id: card.id, direction }) : undefined}
            onResize={canEdit ? (card, columnSpan) => resizeCard.mutate({ id: card.id, columnSpan }) : undefined}
            renderCard={(card) => <DashboardCardTile card={card} sectionTitleById={sectionTitleById} />}
          />
        )}
      </Box>

      <Divider />

      {/* ── Visualizations ── */}
      <Box>
        <Typography
          sx={{
            fontFamily: 'var(--font-sans)',
            fontSize: 18,
            fontWeight: 700,
            color: 'var(--color-ink)',
            mb: 1.5,
          }}
        >
          Visualizations
        </Typography>
        {visualizations.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            No visualizations yet.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: 1.5,
            }}
          >
            {visualizations.map((viz) => (
              <VisualizationThumbCard
                key={viz.id}
                viz={viz}
                sectionTitle={sectionTitleById.get(viz.documentSectionId ?? -1) ?? ''}
                onClick={() => onSelectVisualization(viz)}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* ── Generate/regenerate confirmation ── */}
      <Dialog open={regenConfirmOpen} onClose={() => setRegenConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{cards.length > 0 ? 'Regenerate dashboard cards?' : 'Generate dashboard cards?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {cards.length > 0
              ? "This replaces all of this document's existing dashboard cards with a freshly generated set based on its current content."
              : "This generates dashboard cards — a handful of key facts and figures — from this document's content."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleRegenerate} color="primary" variant="contained" size="small">
            {cards.length > 0 ? 'Regenerate' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete confirmation ── */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete all dashboard cards?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes all {cards.length} dashboard card{cards.length === 1 ? '' : 's'} for this
            document. This can’t be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteCards} color="error" variant="contained" size="small">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function DocumentDetail() {
  const { id } = useParams<{ id: string }>();
  const docId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<'dashboard' | 'content' | 'entities'>('dashboard');
  // Opening another document must start collapsed again — the page stays mounted
  // across navigation, so a stale expand signal would re-open the new sections.
  const { allExpanded, expandSignal, toggleAll: handleToggleAll } = useExpandAll({ resetKey: docId });
  const [editDocOpen, setEditDocOpen] = useState(false);
  const [approvingDoc, setApprovingDoc] = useState(false);
  const [presentationDialogOpen, setPresentationDialogOpen] = useState(false);
  const [presentationInstructions, setPresentationInstructions] = useState('');
  const [presentationSubmitting, setPresentationSubmitting] = useState(false);
  const [presentationError, setPresentationError] = useState<string | null>(null);
  const [presentationColorStyle, setPresentationColorStyle] = useState<VizColorValue>('');
  const [presentationSelectedSections, setPresentationSelectedSections] = useState<Set<number>>(new Set());
  const [gallerySectionId, setGallerySectionId] = useState<number | null>(null);
  const [galleryInitialVizId, setGalleryInitialVizId] = useState<number | null>(null);
  const [addSectionDialogOpen, setAddSectionDialogOpen] = useState(false);
  const [addSectionTitle, setAddSectionTitle] = useState('');
  const [addSectionContent, setAddSectionContent] = useState('');
  const [addSectionParentId, setAddSectionParentId] = useState<number | ''>('');
  const [addSectionSubmitting, setAddSectionSubmitting] = useState(false);
  const [addSectionError, setAddSectionError] = useState<string | null>(null);

  useEffect(() => {
    setActiveTab('dashboard');
  }, [docId]);

  const copyMarkdown = useCopyMarkdown();
  const handleCopyMarkdown = () => {
    if (!doc) return;
    void copyMarkdown(buildDocumentMarkdown(doc.name, rootSections, childrenByParent));
  };

  const docQuery = useQuery({
    queryKey: ['documents', docId],
    queryFn: () => resources.documents.get(docId),
    enabled: Number.isFinite(docId),
  });

  const sectionsQuery = useQuery({
    queryKey: ['document-sections', docId],
    queryFn: () =>
      resources.documentSections.list({
        filter: { documentId: [docId] },
        sort: 'order',
        pageSize: 200,
      }),
    enabled: Number.isFinite(docId),
  });

  const categoriesQuery = useQuery({
    queryKey: ['document-categories'],
    queryFn: () => resources.documentCategories.list({ sort: 'order', pageSize: 100 }),
  });

  const docTypesQuery = useQuery({
    queryKey: ['document-types-all'],
    queryFn: () => resources.documentTypes.list({ pageSize: 500, sort: 'order' }),
  });

  const aiAssistantRolesQuery = useQuery({
    queryKey: ['ai-assistant-roles'],
    queryFn: () => resources.aiAssistantRoles.list({ pageSize: 100 }),
  });

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => resources.users.list({ pageSize: 500 }),
  });

  const commentsQuery = useQuery({
    queryKey: ['document-comments', docId],
    queryFn: () => resources.documentCommentsFor(docId).list({ pageSize: 500 }),
    enabled: Number.isFinite(docId),
  });

  const doc = docQuery.data;

  // Let the AI Assistant show this document's name once it loads.
  useEffect(() => {
    if (doc) updateViewContext({ documentId: doc.id, documentName: doc.name, projectId: doc.projectId });
  }, [doc]);

  const sections = sectionsQuery.data?.data ?? [];
  const allSectionIds = sections.map((s) => s.id);

  const visualizationsQuery = useQuery({
    queryKey: ['document-visualizations', docId],
    queryFn: () =>
      resources.documentVisualizations.list({
        filter: { documentId: docId },
        sort: '-createdAt',
        pageSize: 500,
      }),
    enabled: Number.isFinite(docId),
  });

  const presentationsQuery = useQuery({
    queryKey: ['document-presentations', docId],
    queryFn: () =>
      resources.documentPresentations.list({
        filter: { documentId: [docId] },
        sort: '-createdAt',
        pageSize: 100,
      }),
    enabled: Number.isFinite(docId),
  });

  const presentations = presentationsQuery.data?.data ?? [];
  const generatingPresentation = presentations.find((p) => p.status === 'IN_GENERATION') ?? null;

  // Poll the generating presentation's job so the header shows the current step.
  const presentationJobQuery = useQuery({
    queryKey: ['job', generatingPresentation?.jobId],
    queryFn: () => resources.getJob(generatingPresentation!.jobId!),
    enabled: generatingPresentation?.jobId != null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'PENDING' || status === 'PROCESSING' ? 3000 : false;
    },
  });
  const presentationJobStatus = presentationJobQuery.data?.status;

  // The job moves the presentation out of IN_GENERATION — refetch when it ends.
  useEffect(() => {
    if (presentationJobStatus === 'COMPLETED' || presentationJobStatus === 'FAILED') {
      queryClient.invalidateQueries({ queryKey: ['document-presentations', docId] });
    }
  }, [presentationJobStatus, docId, queryClient]);

  // Build section tree
  const rootSections = sections
    .filter((s) => !s.parentDocumentSectionId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const childrenByParent = new Map<number, DocumentSection[]>();
  sections.forEach((s) => {
    if (s.parentDocumentSectionId != null) {
      const arr = childrenByParent.get(s.parentDocumentSectionId) ?? [];
      arr.push(s);
      childrenByParent.set(s.parentDocumentSectionId, arr);
    }
  });

  const effectiveStatusById = computeEffectiveSectionStatuses(sections, childrenByParent);

  const renderPresentationSectionCheckboxes = (list: DocumentSection[], depth: number): React.ReactNode =>
    list
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((s) => (
        <Box key={s.id}>
          <FormControlLabel
            disabled={presentationSubmitting}
            control={
              <Checkbox
                size="small"
                checked={presentationSelectedSections.has(s.id)}
                onChange={(e) =>
                  setPresentationSelectedSections((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(s.id);
                    else next.delete(s.id);
                    return next;
                  })
                }
                sx={{ p: '4px' }}
              />
            }
            label={<Typography variant="body2">{s.title}</Typography>}
            sx={{ ml: depth * 2 + 0.5, mr: 0 }}
          />
          {renderPresentationSectionCheckboxes(childrenByParent.get(s.id) ?? [], depth + 1)}
        </Box>
      ));

  // Build users map for last-editor avatars
  const usersMap = new Map<number, User>(
    (usersQuery.data?.data ?? []).map((u) => [u.id, u]),
  );

  const allComments: DocumentComment[] = commentsQuery.data?.data ?? [];

  // Group visualizations by their section id
  const visualizationsBySection = new Map<number, DocumentVisualization[]>();
  (visualizationsQuery.data?.data ?? []).forEach((v) => {
    if (v.documentSectionId != null) {
      const arr = visualizationsBySection.get(v.documentSectionId) ?? [];
      arr.push(v);
      visualizationsBySection.set(v.documentSectionId, arr);
    }
  });

  const allVisualizations = visualizationsQuery.data?.data ?? [];
  const sectionTitleById = new Map<number, string>(sections.map((s) => [s.id, s.title]));

  const handleSelectDocumentVisualization = (viz: DocumentVisualization) => {
    if (viz.documentSectionId == null) return;
    setGallerySectionId(viz.documentSectionId);
    setGalleryInitialVizId(viz.id);
  };

  // Resolve category name
  const docType = (docTypesQuery.data?.data ?? []).find((dt) => dt.type === doc?.documentType);
  const category = (categoriesQuery.data?.data ?? []).find(
    (c) => c.type === docType?.categoryType,
  );
  // The AI Assistant Role whose persona matches this document's category —
  // powers the "Ask AI Assistant to help" button's icon/color and dialog.
  const assistantRole: AiAssistantRole | null = (aiAssistantRolesQuery.data?.data ?? []).find(
    (r) => r.documentCategoryType === docType?.categoryType,
  ) ?? null;
  // Flatten the section tree for the "parent section" dropdown, preserving hierarchy order.
  const flattenSectionOptions = (list: DocumentSection[], depth: number): { section: DocumentSection; depth: number }[] =>
    list
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .flatMap((s) => [
        { section: s, depth },
        ...flattenSectionOptions(childrenByParent.get(s.id) ?? [], depth + 1),
      ]);
  const parentSectionOptions = flattenSectionOptions(rootSections, 0);

  const handleDeleteSection = async (sectionId: number) => {
    await resources.documentSections.remove(sectionId);
    queryClient.invalidateQueries({ queryKey: ['document-sections', docId] });
  };

  const pendingApprovalCount = sections.filter((s) => s.status === 'IN_REVIEW').length;

  const handleApproveDocument = async () => {
    setApprovingDoc(true);
    try {
      await resources.approveDocumentSections(docId);
      await queryClient.invalidateQueries({ queryKey: ['document-sections', docId] });
    } finally {
      setApprovingDoc(false);
    }
  };

  const handleGeneratePresentation = async (e: React.FormEvent) => {
    e.preventDefault();
    setPresentationSubmitting(true);
    setPresentationError(null);
    try {
      // Build the full instruction from the color choice + the user's own text.
      // The user never sees the injected style sentence.
      const parts: string[] = [];
      const colorEntry = VIZ_COLOR_STYLES.find((c) => c.value === presentationColorStyle);
      if (colorEntry) parts.push(colorEntry.instruction);
      if (presentationInstructions.trim()) parts.push(presentationInstructions.trim());

      await resources.generateDocumentPresentation(docId, {
        userInstructions: parts.length > 0 ? parts.join(' ') : undefined,
        includedSectionIds: Array.from(presentationSelectedSections),
      });
      await queryClient.invalidateQueries({ queryKey: ['document-presentations', docId] });
      setPresentationDialogOpen(false);
      setPresentationInstructions('');
      setPresentationColorStyle('');
      setPresentationSelectedSections(new Set());
    } catch (err) {
      setPresentationError(extractJobErrorMessage(err));
    } finally {
      setPresentationSubmitting(false);
    }
  };

  // ── Loading ──
  if (docQuery.isLoading || sectionsQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton width={90} height={24} />
          <Skeleton width={80} height={20} />
        </Stack>
        <Skeleton width={340} height={42} />
        <Skeleton height={8} sx={{ borderRadius: 1 }} />
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={56} sx={{ borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  // ── Not found ──
  if (!doc) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 8 }}>
        <Typography variant="h6" color="text.secondary">Document not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>Go back</Button>
      </Box>
    );
  }

  const handleAddSection = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddSectionSubmitting(true);
    setAddSectionError(null);
    try {
      await resources.documentSections.create({
        projectId: doc.projectId,
        documentId: docId,
        documentSectionType: 'CUSTOM',
        title: addSectionTitle.trim(),
        content: addSectionContent.trim() || null,
        parentDocumentSectionId: addSectionParentId || null,
        status: 'BACKLOG',
        importance: 'MEDIUM',
      } as Parameters<typeof resources.documentSections.create>[0]);
      await queryClient.invalidateQueries({ queryKey: ['document-sections', docId] });
      setAddSectionDialogOpen(false);
      setAddSectionTitle('');
      setAddSectionContent('');
      setAddSectionParentId('');
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setAddSectionError(msg ?? 'Failed to add section');
    } finally {
      setAddSectionSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 2.5, pb: 3 }}>

      {/* ── Breadcrumb / meta row ── */}
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
        <IconButton size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>

        {category && (
          <Chip
            label={category.name}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        )}

        <Typography variant="caption" color="text.secondary">
          Updated {formatAge(doc.updatedAt)}
        </Typography>

        {doc.status && (
          <Chip
            label={doc.status.replace('_', ' ')}
            size="small"
            color={
              doc.status === 'APPROVED' ? 'success'
              : doc.status === 'IN_REVIEW' ? 'warning'
              : doc.status === 'ARCHIVED' ? 'error'
              : 'default'
            }
            variant="outlined"
          />
        )}
      </Stack>

      {/* ── Title + action buttons row ── */}
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              sx={{
                fontFamily: 'var(--font-sans)',
                fontSize: 34,
                fontWeight: 800,
                letterSpacing: '-0.025em',
                lineHeight: 1.1,
                color: 'var(--color-ink)',
              }}
            >
              {doc.name}
            </Typography>
            {docType?.description && (
              <Tooltip title={docType.description} arrow placement="right">
                <InfoOutlinedIcon sx={{ fontSize: 20, color: 'text.disabled', cursor: 'help', flexShrink: 0, mt: '4px' }} />
              </Tooltip>
            )}
            {doc.citations && doc.citations.length > 0 && (
              <Tooltip
                title={
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {doc.citations.map((c, i) => (
                      <Box component="li" key={i}>{c.title}</Box>
                    ))}
                  </Box>
                }
                arrow
                placement="right"
              >
                <Badge
                  badgeContent={doc.citations.length}
                  color="primary"
                  sx={{ mt: '4px', '& .MuiBadge-badge': { fontSize: 9, height: 14, minWidth: 14 } }}
                >
                  <FormatQuoteIcon sx={{ fontSize: 20, color: 'text.disabled', cursor: 'help', flexShrink: 0 }} />
                </Badge>
              </Tooltip>
            )}
            <Tooltip title="Edit document" placement="right">
              <IconButton size="small" onClick={() => setEditDocOpen(true)} sx={{ mt: '2px' }}>
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add section" placement="right">
              <IconButton
                size="small"
                onClick={() => {
                  setAddSectionError(null);
                  setAddSectionTitle('');
                  setAddSectionContent('');
                  setAddSectionParentId('');
                  setAddSectionDialogOpen(true);
                }}
                sx={{ mt: '2px' }}
              >
                <AddIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy as Markdown" placement="right">
              <IconButton size="small" onClick={handleCopyMarkdown} sx={{ mt: '2px' }}>
                <ContentCopyIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {presentations.length > 0 && (
              <Tooltip
                title={`Open presentation${presentations.length > 1 ? 's' : ''}`}
                placement="right"
              >
                <IconButton
                  size="small"
                  onClick={() => navigate(`/documents/${docId}/presentation`)}
                  sx={{ mt: '2px' }}
                >
                  <SlideshowIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {pendingApprovalCount > 0 && (
              <Tooltip
                title={`Approve changes — ${pendingApprovalCount} section${pendingApprovalCount > 1 ? 's' : ''} in review`}
                placement="right"
              >
                <span>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={handleApproveDocument}
                    disabled={approvingDoc}
                    sx={{ mt: '2px' }}
                  >
                    <TaskAltIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ flexShrink: 0, pt: 0.5 }}
        >
          {/* Presentation — generate a slide deck for the whole document */}
          {generatingPresentation && (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mr: 0.5 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                {presentationJobQuery.data?.progressMessage ?? 'Preparing…'}
              </Typography>
            </Stack>
          )}
          <Tooltip
            title={
              generatingPresentation
                ? 'A presentation is already being generated'
                : 'Presentation — visualize the whole document as slides'
            }
          >
            <span>
              <Button
                size="small"
                startIcon={<SlideshowIcon />}
                onClick={() => {
                  setPresentationError(null);
                  setPresentationColorStyle('');
                  setPresentationSelectedSections(new Set(allSectionIds));
                  setPresentationDialogOpen(true);
                }}
                disabled={generatingPresentation != null}
              >
                Presentation
              </Button>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 28, alignSelf: 'center' }} />

          {/* Shared users + share button */}
          <SharedUsersBlock docId={docId} />

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 28, alignSelf: 'center' }} />

          {/* Single collapse / expand toggle */}
          <ExpandAllSectionsButton allExpanded={allExpanded} onToggle={handleToggleAll} />
        </Stack>
      </Stack>

      {/* ── Tabs ── */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Dashboard" value="dashboard" />
        <Tab label="Content" value="content" />
        <Tab label="Entities" value="entities" />
      </Tabs>

      {/* ── Dashboard tab ── */}
      {activeTab === 'dashboard' && (
        <DashboardTab
          docId={docId}
          visualizations={allVisualizations}
          sectionTitleById={sectionTitleById}
          onSelectVisualization={handleSelectDocumentVisualization}
        />
      )}

      {/* ── Content tab ── */}
      {activeTab === 'content' && (
        <Paper
          sx={{
            p: 3,
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {rootSections.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              No sections yet — add the first one below.
            </Typography>
          ) : (
            <DocumentSectionList>
              {rootSections.map((s) => (
                <SectionRow
                  key={s.id}
                  section={s}
                  sections={sections}
                  childrenByParent={childrenByParent}
                  effectiveStatusById={effectiveStatusById}
                  expandSignal={expandSignal}
                  usersMap={usersMap}
                  visualizationsBySection={visualizationsBySection}
                  allComments={allComments}
                  onDelete={handleDeleteSection}
                  assistantRole={assistantRole}
                />
              ))}
            </DocumentSectionList>
          )}
        </Paper>
      )}

      {/* ── Entities tab ── */}
      {activeTab === 'entities' && <DocumentEntitiesPanel docId={docId} />}

      {/* ── Add section ── */}
      <FormDialog
        open={addSectionDialogOpen}
        title="Add section"
        onClose={() => setAddSectionDialogOpen(false)}
        onSubmit={handleAddSection}
        submitLabel="Add"
        loading={addSectionSubmitting}
        submitDisabled={!addSectionTitle.trim()}
      >
        <TextField
          label="Title"
          value={addSectionTitle}
          onChange={(e) => setAddSectionTitle(e.target.value)}
          fullWidth
          autoFocus
          disabled={addSectionSubmitting}
        />
        <TextField
          label="Content (optional)"
          value={addSectionContent}
          onChange={(e) => setAddSectionContent(e.target.value)}
          placeholder="Section content…"
          multiline
          minRows={4}
          fullWidth
          disabled={addSectionSubmitting}
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Parent section (optional)</InputLabel>
          <Select
            label="Parent section (optional)"
            value={addSectionParentId}
            onChange={(e) => setAddSectionParentId(e.target.value as number | '')}
            disabled={addSectionSubmitting}
          >
            <MenuItem value=""><em>None — top-level section</em></MenuItem>
            {parentSectionOptions.map(({ section, depth }) => (
              <MenuItem key={section.id} value={section.id} sx={{ pl: 2 + depth * 2 }}>
                {section.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {addSectionError && (
          <Typography variant="caption" color="error">{addSectionError}</Typography>
        )}
      </FormDialog>

      {/* ── Generate-presentation instructions ── */}
      <FormDialog
        open={presentationDialogOpen}
        title="Generate presentation"
        onClose={() => setPresentationDialogOpen(false)}
        onSubmit={handleGeneratePresentation}
        submitLabel="Generate"
        loading={presentationSubmitting}
        submitDisabled={presentationSelectedSections.size === 0}
      >
        <Typography variant="body2" color="text.secondary">
          Turn this document into a slide deck. Existing section visualizations are reused
          where they are still up to date. Optionally choose a color style and the sections
          to include, then add any extra instructions — or leave everything blank.
        </Typography>

        {/* Color style */}
        <FormControl size="small" fullWidth>
          <InputLabel>Color style (optional)</InputLabel>
          <Select
            label="Color style (optional)"
            value={presentationColorStyle}
            onChange={(e) => setPresentationColorStyle(e.target.value as VizColorValue)}
            disabled={presentationSubmitting}
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {VIZ_COLOR_STYLES.map((c) => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sections to include */}
        {rootSections.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
              Include sections
            </Typography>
            <Stack spacing={0}>
              {renderPresentationSectionCheckboxes(rootSections, 0)}
            </Stack>
          </Box>
        )}

        <TextField
          label="Instructions (optional)"
          value={presentationInstructions}
          onChange={(e) => setPresentationInstructions(e.target.value)}
          placeholder="e.g. Keep it under 10 slides; focus on the architecture…"
          multiline
          minRows={3}
          fullWidth
          disabled={presentationSubmitting}
        />
        {presentationError && (
          <Typography variant="caption" color="error">{presentationError}</Typography>
        )}
      </FormDialog>

      {/* ── Edit document dialog ── */}
      <EditDocumentDialog
        open={editDocOpen}
        document={doc}
        onClose={() => setEditDocOpen(false)}
        onDeleted={(deleted) => {
          queryClient.invalidateQueries({ queryKey: ['projects', deleted.projectId] });
          navigate(`/projects/${deleted.projectId}`, { replace: true });
        }}
      />

      {/* ── Picked visualization's section gallery ── */}
      <SectionVisualizationsDialog
        open={gallerySectionId != null}
        onClose={() => { setGallerySectionId(null); setGalleryInitialVizId(null); }}
        sectionTitle={gallerySectionId != null ? (sectionTitleById.get(gallerySectionId) ?? '') : ''}
        visualizations={gallerySectionId != null ? (visualizationsBySection.get(gallerySectionId) ?? []) : []}
        docId={docId}
        initialSelectedId={galleryInitialVizId}
      />
    </Box>
  );
}
